import { useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { IoChatbubbles } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import signerApi from "../../../api/signerapi";
import Pagination from "../../../components/Pagination";
import SearchBar from "../SearchBar";

const CompletedDocs = () => {
  const [docs, setDocs] = useState([]);
  const user = JSON.parse(localStorage.getItem("user"));
  const userEmail = user?.userEmail;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(""); // track the last submitted search
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const fetchCompletedDocs = async () => {
      setLoading(true);
      try {
        let response;
        if (searchTerm && searchTerm.trim() !== "") {
          response = await signerApi.getSearchCompletedDocumentsByEmail(
            userEmail,
            searchTerm,
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          );
        } else {
          response = await signerApi.getCompletedDocumentsByEmail(
            userEmail,
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          );
        }
        const content = response?.data?.content;
        setTotalPages(response.data.totalPages || 0);
        setDocs(Array.isArray(content) ? content : []);
      } catch (error) {
        console.error("Failed to fetch consents:", error);
        setDocs([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedDocs();
  }, [userEmail, searchTerm, page, pageSize, sortedColumn, sortOrder]);

  const handleView = (documentId, documentName, signedFile, fromTab) => {
    if (!documentId || !documentName || !userEmail) return;
    localStorage.setItem("docsActiveTab", fromTab);
    navigate("/dashboard/my-docs/view", {
      state: {
        documentId: documentId,
        documentName: documentName,
        userEmail: userEmail,
        signedFile: signedFile,
        fromTab: fromTab,
      },
    });
  };

  const handleEmail = (signer) => {
    console.log("Emailing for", signer.email);
  };
  const handleDownload = (doc) => {
    const documentName = doc?.documentName || doc?.document?.documentName;
    const signedFile = doc?.signedFile;
    const originalFile = doc?.file || doc?.document?.file;
    const signStatus = doc?.signStatus;

    // Determine which file to download based on sign status
    let base64File;
    if (signStatus === "completed" && signedFile) {
      base64File = signedFile; // Use signed file if available
    } else if (originalFile) {
      base64File = originalFile; // Use original file if signed file is not available
    } else {
      alert("No file available to download.");
      return;
    }

    let downloadBlob;
    if (base64File instanceof Blob) {
      downloadBlob = base64File;
    } else if (typeof base64File === "string") {
      // Check if the string is a valid base64 string
      if (base64File.startsWith("data:application/pdf;base64,")) {
        // Remove the prefix if it exists
        base64File = base64File.split(",")[1];
      }
      try {
        const byteCharacters = atob(base64File);
        const byteArrays = [];
        const sliceSize = 512;

        for (
          let offset = 0;
          offset < byteCharacters.length;
          offset += sliceSize
        ) {
          const slice = byteCharacters.slice(offset, offset + sliceSize);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          byteArrays.push(new Uint8Array(byteNumbers));
        }

        downloadBlob = new Blob(byteArrays, { type: "application/pdf" });
      } catch (error) {
        console.error("Error decoding file:", error);
        alert("Invalid file format. Cannot download.");
        return;
      }
    } else {
      alert("Unsupported file format.");
      return;
    }

    const suffix =
      signStatus === "completed" && signedFile ? "_signed" : "_original";
    const fileName =
      (documentName || "document").replace(/\s+/g, "_") + suffix + ".pdf";

    const url = URL.createObjectURL(downloadBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  const handleSearch = (value) => {
    setPage(0);
    setSearchTerm((value ?? searchQuery).trim());
  };
  if (loading) return <p>Loading Completed Docs...</p>;
  const handleChatHubClick = (documentId, documentName) => {
    navigate("/dashboard/chat-app-signers", {
      state: {
        documentId,
        documentName,
        chatType: "document",
        activeSidebar: "/dashboard/my-docs",
      },
    });
  };

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0);
  };

  const handlePageClick = (data) => {
    const selectedPage = Math.max(0, Math.min(data.selected, totalPages - 1)); // Ensure selectedPage is within range
    setPage(selectedPage);
    localStorage.setItem("completedDocs", selectedPage); // Store the page number in localStorage
  };

  const handleSort = (column) => {
    if (sortedColumn === column) {
      // Toggle sort order if the same column is clicked
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Sort by the new column (default to ascending)
      setSortedColumn(column);
      setSortOrder("asc");
    }
  };

  const handleDocumentClick = (documentId, documentName, signedFile) => {
    if (!signedFile) {
      Swal.fire("Error", "Document file not available", "error");
      return;
    }

    try {
      const byteCharacters = atob(signedFile);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      // PDF file
      const blob = new Blob([byteArray], {
        type: "application/pdf",
      });

      const fileURL = URL.createObjectURL(blob);

      window.open(fileURL, "_blank");
    } catch (error) {
      console.error("Failed to open document:", error);
      Swal.fire("Error", "Unable to open document", "error");
    }
  };
  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
        }}
      >
        <div style={{ width: "250px" }}>
          <SearchBar
            placeholder="Search completed docs..."
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch}
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          striped
          bordered
          hover
          style={{ minWidth: "1100px", whiteSpace: "nowrap" }}
        >
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th>#</th>
              <th>Document Name</th>
              <th>Date Created</th>
              <th>Signed On</th>
              <th>Sign Status</th>
              <th>Chat</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr>
                <td colSpan="5">No documents found.</td>
              </tr>
            ) : (
              docs.map((doc, index) => {
                const documentName =
                  doc?.documentName || doc?.document?.documentName;
                const createdDate =
                  doc?.createdDate || doc?.document?.createdDate;
                const signedAt =
                  doc?.signedAt ?? doc?.signedAt ?? "Not signed yet";
                const signStatus = doc?.signStatus;

                return (
                  <tr key={index}>
                    <td>{page * pageSize + index + 1}</td>
                    <td>
                      <a
                        href="#"
                        style={{
                          color: "blue",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "600",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleDocumentClick(
                            doc.documentId,
                            documentName,
                            doc.signedFile,
                          );
                        }}
                      >
                        {documentName || "N/A"}
                      </a>
                    </td>
                    <td>{createdDate || "N/A"}</td>
                    <td>{signedAt || "Not signed yet"}</td>
                    <td>{signStatus || "completed"}</td>

                    <td>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          handleChatHubClick(doc.documentId, documentName)
                        }
                        title="Chat"
                      >
                        <IoChatbubbles />
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      <div style={{ marginTop: "auto" }}>
        {docs.length > 0 && totalPages > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            handlePageSizeChange={handlePageSizeChange}
            handlePageClick={handlePageClick}
          />
        )}
      </div>
    </div>
  );
};

export default CompletedDocs;
